/**
 * TypeScript models for chat feature (Issue #263).
 *
 * These mirror the proto messages defined in src/main/proto/chat.proto.
 *
 * WHY MANUALLY CREATED (NOT AUTO-GENERATED)?
 *
 * Unlike other services (ArchitecturalPlanService, etc.) which use grpc-web client
 * generation, the chat service uses a different transport:
 *
 * 1. TRANSPORT: SSE (Server-Sent Events) over HTTP, not grpc-web
 *    - ChatService streams responses via SSE (text/event-stream)
 *    - ESPv2 automatically transcodes gRPC server-streaming → SSE
 *    - Frontend uses EventSource API, not grpc-web client
 *
 * 2. API PATTERN: REST/SSE, not RPC
 *    - POST /v1/chat/sessions/{id}/stream (SSE streaming)
 *    - GET /v1/chat/sessions/{id} (REST)
 *    - No need for grpc-web service client stubs
 *
 * 3. SIMPLICITY: Manual interfaces are cleaner
 *    - These ~100 lines define simple TypeScript interfaces
 *    - Auto-generated grpc-web would be 500+ lines with RPC methods
 *    - We only need data shapes for JSON responses
 *
 * 4. MAINTENANCE: Easier to understand and modify
 *    - Direct mapping to JSON responses from ESPv2
 *    - No build-time proto→TypeScript compilation needed
 *    - Changes to chat.proto → manually update these interfaces
 *
 * See: web-ng-m3/scripts/generate-step-metadata.js (excludes chat.proto)
 * See: src/main/proto/chat.proto (proto definitions)
 */

export interface ChatSession {
  sessionId: string;
  userId: string;
  projectId?: string;
  projectName?: string;
  createdAt: Date;
  lastMessageAt: Date;
  messageCount: number;
  agentName: string;
  modelName: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  title?: string;
}

export interface ChatMessage {
  messageId: string;
  sessionId?: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  timestamp: Date;
  metadata?: MessageMetadata;
  modality?: 'text' | 'voice'; // Origin modality (Issue #845); absent implies "text"
  queued?: boolean; // True if message is queued, not yet sent
  decorating?: boolean; // True if decoration is in progress
  streaming?: boolean; // True while response chunks are still arriving
  progress?: string; // Latest server progress note while the turn runs silently (Issue #2989)
  failed?: boolean; // True when this assistant turn ended without a response
  retryable?: boolean; // True when the original request snapshot is available for retry
  errorCode?: string; // Stable application-owned diagnostic code for a failed turn
  errorCorrelationId?: string; // Safe identifier that links the UI failure to server logs
  errorAction?: 'OPEN_BILLING'; // Allowlists the recovery action the UI may offer
  attachments?: ChatAttachment[]; // Ordered image attachments sent with this turn (Issue #1746)
  selectedTexts?: SelectedTextContext[]; // Document text quote context (Issue #1745)
}

export interface MessageMetadata {
  thinkingContent?: string; // Deprecated: Use timeline instead
  toolCalls?: ToolCall[]; // Deprecated: Use timeline instead
  timeline?: TimelineEvent[]; // Chronological events (thinking + tool calls)
  costAnalysis?: CostAnalysis;
  latencyMs?: number;
  reasoningUnavailable?: boolean; // HIGH thinking requested, but provider returned no reasoning parts
}

export interface TimelineEvent {
  type: 'thinking' | 'tool_call';
  timestamp?: number; // Milliseconds since message start
  content?: string; // For thinking events
  toolCall?: ToolCall; // For tool call events
  durationMs?: number; // Duration of the event (e.g. thinking time)
  summary?: string; // LLM-generated short summary (3-8 words)
}

export type ToolCallStatus = 'STARTING' | 'IN_PROGRESS' | 'COMPLETED' | 'NOT_FOUND' | 'FAILED' | 'TIMED_OUT';

export const TOOL_CALL_STATUS = {
  STARTING: 'STARTING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  NOT_FOUND: 'NOT_FOUND',
  FAILED: 'FAILED',
  /** Issue #3356: voice only; the assistant stopped waiting for the tool and told the user. */
  TIMED_OUT: 'TIMED_OUT',
} as const satisfies Record<string, ToolCallStatus>;

export interface ToolCall {
  toolName: string;
  operationId: string;
  status?: ToolCallStatus;
  parameters: Record<string, any>;
  result?: any;
  error?: string;
  durationMs: number;
  summary?: string; // LLM-generated short summary (3-8 words)
  /** Issue #3356: the result arrived after the call timed out, so the assistant never saw it. */
  finishedAfterTimeout?: boolean;
}

export interface TokenTypeMeta {
  tokenCount?: number;
  costUsd?: number;
  ratePerMillion?: number;
  discountPercent?: number;
}

export interface TokenBreakdown {
  nonCachedInput?: TokenTypeMeta;
  cachedContent?: TokenTypeMeta;
  output?: TokenTypeMeta;
  thinking?: TokenTypeMeta;
  toolUse?: TokenTypeMeta;
}

/**
 * Cost analysis from a single chat turn or accumulated session total.
 * All numeric fields are optional because proto3 JSON (JsonFormat.printer())
 * omits default values (0, "", false), so they may be absent in the payload.
 */
export interface CostAnalysis {
  totalTokens?: number;
  tokenBreakdown?: TokenBreakdown;
  estimatedTotalCostUsd?: number;
  model?: string;
  contextWindowSize?: number;
}

/**
 * Absolute token usage snapshot from the Gemini Live API. Unlike TokenUsage which
 * may carry optional fields and is accumulated per-turn, this represents the complete
 * context state at a point in time with all fields required.
 */
export interface VoiceTokenUsage {
  promptTokens: number;
  candidatesTokens: number;
  totalTokens: number;
  contextWindowSize: number;
  estimatedTotalCostUsd?: number;
  model?: string;
}

export interface SendMessageRequest {
  message: string;
  context?: ChatContext;
  modelName?: string;
  thinkingLevel?: string;
  thinkingBudget?: number;
  /**
   * Ordered IDs of already-uploaded image attachments (Issue #1746). Only identifiers travel
   * here; the bytes live in private storage and the backend resolves them after checking that
   * the signed-in user owns each one.
   */
  attachmentIds?: string[];
}

export type ThinkingMode = 'BUDGET' | 'LEVEL';

export interface ChatModelOption {
  id: string;
  label: string;
  thinkingMode?: ThinkingMode;
  voiceOnly?: boolean;
  defaultVoice?: boolean;
  contextWindowSize?: number;
  supportedThinkingLevels?: string[];
  /**
   * Whether this model can read images in a user turn (Issue #1746). The composer uses it to
   * explain why attachments are unavailable on a voice model rather than dropping them silently.
   */
  imageInputSupported?: boolean;
}

export interface ThinkingLevelOption {
  id: string;
  label: string;
}

export const DEFAULT_CHAT_MODEL_ID = 'gemini-3.8-flash';
export const DEFAULT_THINKING_LEVEL = 'MEDIUM';

export const THINKING_BUDGET_OPTIONS: ThinkingLevelOption[] = [
  { id: 'OFF', label: 'Off' },
  { id: 'LOW', label: 'Low' },
  { id: 'MEDIUM', label: 'Medium' },
  { id: 'HIGH', label: 'High' },
  { id: 'CUSTOM', label: 'Custom' },
];

const LEVEL_LABELS: Record<string, string> = {
  MINIMAL: 'Minimal',
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

/**
 * Fallback for LEVEL-mode models whose supportedThinkingLevels aren't populated
 * (e.g., stale /v1/models API cache before backend is redeployed).
 */
export const THINKING_GEMINI3_DEFAULT_OPTIONS: ThinkingLevelOption[] = ['LOW', 'HIGH'].map(id => ({
  id,
  label: LEVEL_LABELS[id],
}));

/**
 * Builds thinking level options from a model's supported levels (Issue #1009).
 * Falls back to LOW/HIGH if the model doesn't report supported levels.
 * Unknown level IDs not present in LEVEL_LABELS are silently dropped.
 */
export function buildThinkingLevelOptions(supportedLevels: string[] | undefined): ThinkingLevelOption[] {
  if (!supportedLevels || supportedLevels.length === 0) {
    return THINKING_GEMINI3_DEFAULT_OPTIONS;
  }
  return supportedLevels.filter(id => id in LEVEL_LABELS).map(id => ({ id, label: LEVEL_LABELS[id] }));
}

export interface ChatContext {
  projectId?: string;
  projectName?: string;
  projectAddress?: string;
  fileId?: string;
  fileName?: string;
  pageNumber?: number;
  tab?: string;
  pinnedPages?: PinnedPage[];
  selectedTexts?: SelectedTextContext[];
  additionalContext?: Record<string, any>;
}

export interface SelectedTextBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  rotationDegrees?: number;
}

export interface SelectedTextContext {
  id?: string;
  projectId?: string;
  fileId?: string;
  fileName?: string;
  pageNumber: number;
  ocrRevision?: string;
  selector?: {
    v: 1;
    exact: string;
    occurrence?: number;
    prefix?: string;
    suffix?: string;
    fingerprint?: string;
  };
  selectedText: string;
  excerpt?: string;
  regionPath?: string;
  boundingBoxes?: SelectedTextBoundingBox[];
}

export interface PinnedPage {
  pageNumber: number;
  pageTitle?: string;
  fileId?: string;
  fileName?: string;
  pinnedAt: Date;
}

export interface ChatMessageResponse {
  messageId?: string;
  content?: string;
  type:
    | 'TEXT'
    | 'THINKING'
    | 'THINKING_SUMMARY'
    | 'TOOL_CALL_START'
    | 'TOOL_CALL_RESULT'
    | 'COST_UPDATE'
    | 'MESSAGE_UPDATE'
    | 'PROGRESS'
    | 'ERROR';
  metadata?: MessageMetadata;
  isFinal: boolean;
  reasoningUnavailable?: boolean;
  errorCode?: string;
  errorCorrelationId?: string;
  errorAction?: 'OPEN_BILLING';
  retryable?: boolean;

  // For thinking tokens
  thinkingContent?: string;

  // For inline thinking summaries (THINKING_SUMMARY chunks)
  summary?: string;
  timelineEventIndex?: number;

  // Cost analysis for this turn (Issue #691: unified with task system)
  costAnalysis?: CostAnalysis;

  // For tool call progress
  toolCall?: {
    toolName: string;
    operationId: string;
    status: ToolCallStatus;
    parameters?: Record<string, any>;
    result?: any;
    error?: string;
    durationMs?: number;
  };
}

export interface CreateSessionRequest {
  projectId?: string;
  metadata?: Record<string, string>;
  modelName?: string;
}

export interface CreateSessionResponse {
  sessionId: string; // API returns camelCase (JSON naming)
  createdAt: string; // ISO 8601 string from API
}

export interface GetSessionHistoryResponse {
  session?: ChatSession;
  messages?: ChatMessage[];
  totalMessages?: number;
  hasMore?: boolean;
}

export interface ListSessionsResponse {
  sessions: ChatSession[];
}

// ─── Voice Chat (Issue #802) ──────────────────────────────────────────

/** Payload for async-decorated voice transcripts pushed via WebSocket (Issue #941). */
export interface VoiceTranscriptDecoratedEvent {
  messageId: string;
  content: string;
  rawContent: string;
}

/** Payload for post-hoc corrected user voice transcripts pushed via WebSocket (Issue #880). */
export interface VoiceTranscriptCorrectedEvent {
  messageId: string;
  content: string;
  rawContent: string;
}

export type VoiceChatState = 'idle' | 'connecting' | 'reconnecting' | 'active' | 'error';

export interface VoiceChatConfig {
  voice?: string; // Gemini voice name (default: "Aoede")
  language?: string; // BCP-47 language code (default: "en-US")
  model?: string; // Backend-advertised Gemini Live model identifier
}

export interface VoiceHistoryEntry {
  role: 'USER' | 'ASSISTANT';
  content: string;
}

export interface VoiceMessage {
  type: 'start' | 'audio' | 'stop' | 'ping';
  data?: string; // base64-encoded PCM for "audio" messages
  sessionId?: string;
  config?: VoiceChatConfig;
}

export interface VoiceResponse {
  type:
    | 'audio'
    | 'transcript'
    | 'transcript_decorated'
    | 'transcript_corrected'
    | 'status'
    | 'error'
    | 'interrupted'
    | 'auth_ok'
    | 'tool_call_start'
    | 'tool_call_result'
    | 'thinking'
    | 'token_usage'
    | 'pong'
    | 'inactivity_warning'
    | 'turn_complete';
  data?: string; // base64-encoded PCM for "audio" responses
  role?: 'user' | 'assistant';
  text?: string; // transcription text or thinking content
  partial?: boolean | string;
  state?: 'listening' | 'speaking' | 'processing' | 'ended';
  resumed?: boolean;
  turnId?: string;
  reason?: string; // reason for status changes (e.g. 'inactivity')
  secondsRemaining?: number; // inactivity warning countdown
  message?: string; // error message
  messageId?: string; // Firestore message ID (Issue #941: decoration)
  content?: string; // decorated content (Issue #941: decoration)
  rawContent?: string; // original raw content before decoration (Issue #941)

  // Tool call fields
  toolName?: string;
  operationId?: string;
  parameters?: string; // JSON string of tool call parameters
  result?: string; // JSON string of tool call result
  /** Issue #3356: 'TIMED_OUT' when the voice service stopped waiting for the tool. */
  status?: 'TIMED_OUT';
  /** Issue #3356: the deadline that passed, in seconds. */
  timeoutSeconds?: number;
  /** Issue #3356: this result arrived after the call timed out; the model never received it. */
  late?: boolean;
}

// ─── Image attachments (Issue #1746) ──────────────────────────────────

/**
 * Where an image attachment is in its life.
 *
 * A draft starts as `uploading` while its bytes travel to private storage, becomes `validating`
 * while the backend proves the file signature and strips camera metadata, and reaches `ready` only
 * when it can actually be sent. `failed` and `expired` are shown explicitly rather than hidden,
 * because an attachment that quietly disappears would let someone send a question about a picture
 * the assistant never received.
 */
export type ChatAttachmentState = 'uploading' | 'validating' | 'ready' | 'failed' | 'expired';

/** Lifecycle values the backend reports, mapped onto the UI states above. */
export const BACKEND_ATTACHMENT_STATE: Record<string, ChatAttachmentState> = {
  ATTACHMENT_PENDING_UPLOAD: 'uploading',
  ATTACHMENT_READY: 'ready',
  ATTACHMENT_FAILED: 'failed',
  ATTACHMENT_EXPIRED: 'expired',
};

/**
 * One image attached to a chat turn.
 *
 * Note what is absent: there is no storage path and no long-lived URL. `previewUrl` is either a
 * browser-local object URL for a draft that has not been sent yet, or a short-lived signed URL
 * fetched on demand for a sent message, and it is released as soon as the attachment is removed or
 * the component is torn down.
 */
export interface ChatAttachment {
  /** Stable, opaque identifier issued by the backend. */
  attachmentId: string;
  /** Effective MIME type, proven by the file's own signature after validation. */
  mimeType: string;
  /** Size in bytes of the stored image. */
  byteSize: number;
  /** Decoded pixel width; zero until the backend has validated the file. */
  width: number;
  /** Decoded pixel height; zero until the backend has validated the file. */
  height: number;
  /** Original file name, or empty for an image that arrived through the clipboard. */
  displayName: string;
  /** Current lifecycle state. */
  state: ChatAttachmentState;
  /** Plain-language explanation shown when the state is `failed` or `expired`. */
  failureReason?: string;
  /** Upload progress from 0 to 100, present while the bytes are in flight. */
  progress?: number;
  /** Short-lived or browser-local URL used to render the thumbnail and preview. */
  previewUrl?: string;
  /** Where the image was cropped from, for a server-rendered Preview region (Issue #1747). */
  pageRegion?: PageRegionSource;
}

// ─── Page region attachments (Issue #1747) ─────────────────────────────

/**
 * A point in unrotated page-normalized coordinates.
 *
 * `x` and `y` run from 0 to 1 across the page, with the origin at the page's top-left corner before
 * its rotation is applied. Zoom, scroll, device pixel ratio, layout and the Preview's rotate action
 * never change these numbers, which is what lets a stored region stay on the same drawing content.
 */
export interface NormalizedPoint {
  x: number;
  y: number;
}

/** An axis-aligned rectangle in the same unrotated page-normalized space. */
export interface NormalizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** How a region was drawn. */
export type PageRegionShapeName = 'lasso' | 'rectangle';

/** The page source recorded with a server-rendered region crop. Never changes after capture. */
export interface PageRegionSource {
  projectId: string;
  fileId: string;
  fileName: string;
  pageNumber: number;
  /** Rotation-independent fingerprint of the page content at capture. */
  pageRevision: string;
  shape: PageRegionShapeName;
  polygon: NormalizedPoint[];
  boundingBox: NormalizedRect;
  /** The area actually rendered: bounding box plus disclosed context padding. */
  cropBox: NormalizedRect;
  /** The page's clockwise display rotation at capture; the image is turned to match. */
  displayRotationDegrees: number;
  /** Effective resolution of the crop, in dots per PDF inch. */
  renderDpi: number;
  cropRevision: string;
  label: string;
  description?: string;
}

/**
 * A chat image attachment whose pixels are a Preview region crop.
 *
 * This is the reusable result of `PageRegionCropService.createCrop()`: any workflow that asks for a
 * page-region image receives this shape, whether or not the image ends up in chat.
 */
export interface PageRegionAttachment extends ChatAttachment {
  pageRegion: PageRegionSource;
}

/** Whether an attachment carries a page source. */
export function isPageRegionAttachment(attachment: ChatAttachment): attachment is PageRegionAttachment {
  return !!attachment.pageRegion;
}

/** The page source as it arrives over the wire (proto3 JSON omits zero values). */
export interface RawPageRegionSource {
  projectId?: string;
  fileId?: string;
  fileName?: string;
  pageNumber?: number;
  pageRevision?: string;
  shape?: string;
  polygon?: Array<{ x?: number; y?: number }>;
  boundingBox?: { x?: number; y?: number; width?: number; height?: number };
  cropBox?: { x?: number; y?: number; width?: number; height?: number };
  displayRotationDegrees?: number;
  renderDpi?: number;
  cropRevision?: string;
  label?: string;
  description?: string;
}

/** Status of a region attachment's source page, as reported by the backend. */
export interface PageRegionSourceStatus {
  attachmentId: string;
  source: PageRegionSource | null;
  stale: boolean;
  pageAvailable: boolean;
  message?: string;
}

/** Fills proto3's omitted zero values in a wire rectangle. */
function toNormalizedRect(raw: RawPageRegionSource['boundingBox']): NormalizedRect {
  return { x: raw?.x ?? 0, y: raw?.y ?? 0, width: raw?.width ?? 0, height: raw?.height ?? 0 };
}

/** Converts a wire page source into the UI shape, filling proto3's omitted zero values. */
export function toPageRegionSource(raw: RawPageRegionSource | undefined | null): PageRegionSource | undefined {
  if (!raw) return undefined;
  return {
    projectId: raw.projectId ?? '',
    fileId: raw.fileId ?? '',
    fileName: raw.fileName ?? '',
    pageNumber: raw.pageNumber ?? 0,
    pageRevision: raw.pageRevision ?? '',
    shape: raw.shape === 'PAGE_REGION_RECTANGLE' ? 'rectangle' : 'lasso',
    polygon: (raw.polygon ?? []).map(point => ({ x: point?.x ?? 0, y: point?.y ?? 0 })),
    boundingBox: toNormalizedRect(raw.boundingBox),
    cropBox: toNormalizedRect(raw.cropBox),
    displayRotationDegrees: raw.displayRotationDegrees ?? 0,
    renderDpi: raw.renderDpi ?? 0,
    cropRevision: raw.cropRevision ?? '',
    label: raw.label ?? '',
    description: raw.description || undefined,
  };
}

/** Limits mirrored from the backend so the composer can refuse a bad file immediately. */
export const ATTACHMENT_LIMITS = {
  supportedMimeTypes: ['image/png', 'image/jpeg'] as const,
  /** File input `accept` value, kept in step with `supportedMimeTypes`. */
  acceptAttribute: 'image/png,image/jpeg',
  maxBytesPerFile: 10 * 1024 * 1024,
  maxAttachmentsPerMessage: 4,
  maxDimensionPixels: 8192,
} as const;

/** Backend response when a new attachment is staged. */
export interface CreateAttachmentResponse {
  attachment: RawImageAttachment;
  uploadUrl: string;
  uploadContentType: string;
  uploadUrlExpiresAt?: string | number;
}

/** Backend response carrying a short-lived signed read URL. */
export interface AttachmentUrlResponse {
  url: string;
  expiresAt?: string | number;
  attachment?: RawImageAttachment;
}

/** The attachment shape as it arrives over the wire, before UI-state mapping. */
export interface RawImageAttachment {
  attachmentId: string;
  mimeType?: string;
  byteSize?: string | number;
  width?: number;
  height?: number;
  displayName?: string;
  state?: string;
  failureReason?: string;
  pageRegion?: RawPageRegionSource;
}

/** Label shown for an image that came from the clipboard and has no file name. */
export const PASTED_IMAGE_LABEL = 'Pasted image';

/** Human-readable file size, used in thumbnails and screen-reader labels. */
export function formatAttachmentSize(byteSize: number): string {
  if (!byteSize || byteSize <= 0) return '';
  if (byteSize >= 1024 * 1024) return `${(byteSize / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.round(byteSize / 1024)} KB`;
}

/**
 * Converts the wire shape into the UI shape.
 *
 * Proto3 JSON omits default values, so an absent field means zero or empty rather than missing
 * data, and `byteSize` arrives as a string because it is a 64-bit integer.
 */
export function toChatAttachment(raw: RawImageAttachment): ChatAttachment {
  return {
    attachmentId: raw.attachmentId,
    mimeType: raw.mimeType ?? '',
    byteSize: typeof raw.byteSize === 'string' ? parseInt(raw.byteSize, 10) || 0 : (raw.byteSize ?? 0),
    width: raw.width ?? 0,
    height: raw.height ?? 0,
    displayName: raw.displayName ?? '',
    state: BACKEND_ATTACHMENT_STATE[raw.state ?? ''] ?? 'failed',
    failureReason: raw.failureReason || undefined,
    ...(raw.pageRegion ? { pageRegion: toPageRegionSource(raw.pageRegion) } : {}),
  };
}
